import React, { useState } from 'react';
import { Context, updateContext, deleteContext } from '../services/contextService';
import ViewContextModal from './ViewContextModal';
import { FaGlobe, FaFileAlt, FaPen, FaTrash, FaPlus, FaCalendarAlt } from 'react-icons/fa';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import './ContextList.css';

interface ContextListProps {
  contexts: Context[];
  onAddContext: () => void;
}

const ContextList: React.FC<ContextListProps> = ({ contexts, onAddContext }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedContext, setSelectedContext] = useState<Context | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (contextId: number) => deleteContext(projectId!, contextId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`Failed to delete context: ${error.message}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ contextId, name }: { contextId: number; name: string }) => updateContext(projectId!, contextId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`Failed to update context: ${error.message}`);
    }
  });

  const handleCardClick = (context: Context) => {
    setSelectedContext(context);
    setIsViewModalOpen(true);
  };

  const handleEditClick = (e: React.MouseEvent, context: Context) => {
    e.stopPropagation();
    const newName = prompt("Enter the new name for the context:", context.name);
    if (newName && newName !== context.name) {
      updateMutation.mutate({ contextId: context.id, name: newName });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, contextId: number) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this context? This action cannot be undone.")) {
      deleteMutation.mutate(contextId);
    }
  };

  const handleCloseModal = () => {
    setIsViewModalOpen(false);
    setSelectedContext(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTypeIcon = (type: string) => {
    return type === 'url' ? <FaGlobe /> : <FaFileAlt />;
  };

  const getTypeLabel = (type: string) => {
    return type === 'url' ? 'URL' : 'Texto';
  };

  return (
    <>
      <div className="ctx__grid">
        {contexts.map((context) => (
          <div
            key={context.id}
            className={`ctx__card ctx__card--${context.type}`}
            onClick={() => handleCardClick(context)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick(context);
              }
            }}
          >
            <div className="ctx__accent" />

            <div className="ctx__header">
              <div className={`ctx__icon ctx__icon--${context.type}`}>
                {getTypeIcon(context.type)}
              </div>
              <div className="ctx__info">
                <span className="ctx__name" title={context.name}>
                  {context.name}
                </span>
                <span className={`ctx__status ctx__status--${context.status.toLowerCase()}`}>
                  {context.status}
                </span>
              </div>
            </div>

            {context.type === 'url' && context.source && (
              <div className="ctx__source" title={context.source}>
                {context.source}
              </div>
            )}

            <div className="ctx__meta">
              <FaCalendarAlt style={{ fontSize: '0.65rem' }} />
              {formatDate(context.creation_date)}
              <span style={{ margin: '0 4px', color: '#d1d5db' }}>|</span>
              {getTypeLabel(context.type)}
            </div>

            <div className="ctx__actions">
              <button
                className="ctx__action-btn ctx__action-btn--edit"
                title="Edit name"
                onClick={(e) => handleEditClick(e, context)}
              >
                <FaPen />
              </button>
              <button
                className="ctx__action-btn ctx__action-btn--delete"
                title="Delete context"
                onClick={(e) => handleDeleteClick(e, context.id)}
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}

        {/* Add Context CTA card */}
        <div
          className="ctx__add-card"
          role="button"
          tabIndex={0}
          onClick={onAddContext}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAddContext();
            }
          }}
        >
          <div className="ctx__add-icon">
            <FaPlus />
          </div>
          <span className="ctx__add-label">Add Context</span>
        </div>
      </div>

      {selectedContext && (
        <ViewContextModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          context={selectedContext}
        />
      )}
    </>
  );
};

export default ContextList;
